import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  Receipt,
  CreditCard,
  XCircle,
  Loader2,
  CalendarClock,
  Lock,
  Package,
  ArrowRight,
  Building2,
  FileText,
  Download,
  Trash2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { HelpIcon } from "@/components/HelpIcon";

/**
 * Billing — invoice-first model (v3).
 *
 * Modes:
 *   • Invoice (default) — managers add tenants/vehicles, then "Submit for
 *     Invoice" → backend builds a PDF + Stripe payment link. Vehicles stay in
 *     pending_invoice until the invoice is marked paid (manually by admin or
 *     via Stripe checkout).
 *   • Auto Pay — original flow: pay the $99 setup fee then a monthly
 *     subscription via Stripe Checkout.
 *
 *   Pricing (v2, Feb 2026):
 *   • $99 one-time setup fee.
 *   • First 30 days after setup paid = FREE.
 *   • $1.00 / active car / month (volume discounts at 200/500 cars; 1000+ quote).
 */
export default function Billing() {
  const { activeProperty, refreshProperties } = useAuth();
  const nav = useNavigate();
  const [details, setDetails] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    if (!activeProperty) return;
    try {
      const [d, inv] = await Promise.all([
        api.get(`/billing/details?property_id=${activeProperty.id}`),
        api.get(`/billing/invoices?property_id=${activeProperty.id}`),
      ]);
      setDetails(d.data);
      setInvoices(inv.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [activeProperty]);

  const sessionId = params.get("session_id");
  const canceled = params.get("canceled");
  const redirectAfter = params.get("redirect_after");

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (canceled) {
      toast.info("Checkout canceled.");
      params.delete("canceled");
      setParams(params, { replace: true });
    }
  }, [canceled, params, setParams]);

  // Poll Stripe Checkout result up to 90s with live countdown toast.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 45;   // 90s @ 2s interval — Stripe can lag ~10s
    const POLL_MS = 2000;
    const MAX_S = (MAX_ATTEMPTS * POLL_MS) / 1000;
    const tid = toast.loading(`Verifying payment… ${MAX_S}s`, { duration: Infinity });
    const poll = async () => {
      if (cancelled) return;
      if (attempts >= MAX_ATTEMPTS) {
        // Clear the spinner and surface the exact copy the spec requires.
        toast.dismiss(tid);
        toast.error(
          "Payment received but account activation is still processing. Contact support.",
          {
            duration: 15000,
            action: {
              label: "Check again",
              onClick: () => {
                // Retry verification from scratch by re-reading the
                // session_id — useful if Stripe just caught up.
                setParams(new URLSearchParams(params), { replace: true });
                window.location.reload();
              },
            },
          },
        );
        // Clean the URL so a stale session_id doesn't re-trigger polling.
        params.delete("session_id");
        params.delete("redirect_after");
        setParams(params, { replace: true });
        return;
      }
      attempts += 1;
      const remaining = Math.max(0, MAX_S - attempts * (POLL_MS / 1000));
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Stop the countdown IMMEDIATELY; refresh data afterwards.
          toast.dismiss(tid);
          await refreshProperties();
          await load();
          let activeUntilStr = "";
          try {
            const fresh = await api.get(
              `/billing/details?property_id=${activeProperty.id}`
            );
            const u = fresh.data?.subscription?.active_until;
            if (u) activeUntilStr = new Date(u).toLocaleDateString();
          } catch {}
          if (data.kind === "setup_fee") {
            toast.success("Setup complete — you can now add tenants and vehicles.", { duration: 6000 });
          } else if (data.kind === "subscription" && activeUntilStr) {
            toast.success(`Payment received — your account is active until ${activeUntilStr}`, { duration: 6000 });
          } else if (data.kind === "invoice") {
            toast.success("Invoice paid — your vehicles are now active", { duration: 6000 });
          } else {
            toast.success("Payment received", { duration: 6000 });
          }
          params.delete("session_id");
          params.delete("redirect_after");
          setParams(params, { replace: true });
          if (redirectAfter === "onboard") {
            setTimeout(() => nav("/dashboard/onboard"), 800);
          }
          return;
        }
        if (data.payment_status === "processing") {
          // ACH submitted — stop the countdown and surface the
          // "Payment processing" state. The bank typically settles in
          // 4-5 business days, after which the payment_intent.succeeded
          // webhook flips the invoice to paid and activates QRs.
          toast.dismiss(tid);
          await refreshProperties();
          await load();
          toast.message(
            "Payment processing — your bank is verifying the transfer. We'll activate your account within 4–5 business days.",
            { duration: 10000 },
          );
          params.delete("session_id");
          params.delete("redirect_after");
          setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.dismiss(tid);
          toast.error("Checkout session expired. Please try again.", { duration: 8000 });
          params.delete("session_id");
          params.delete("redirect_after");
          setParams(params, { replace: true });
          return;
        }
      } catch {
        // Ignore intermittent errors and keep polling — the countdown
        // toast will remain so the user sees we're still trying.
      }
      toast.loading(`Verifying payment… ${remaining}s`, { id: tid, duration: Infinity });
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      toast.dismiss(tid);
    };
    // eslint-disable-next-line
  }, [sessionId]);

  const paySetupFee = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/setup-fee", {
        property_id: activeProperty.id,
        origin_url: window.location.origin,
        redirect_after: "onboard",
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const removePaymentMethod = async () => {
    if (!activeProperty) return;
    const ok = window.confirm(
      "Remove the saved card on file? This also strips card details from past invoice records. " +
        "You'll be asked for a card again the next time you pay."
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(
        `/billing/payment-method?property_id=${activeProperty.id}`
      );
      toast.success("Saved card removed.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const payMonthly = async ({ redirectToOnboard = "auto" } = {}) => {
    setBusy(true);
    try {
      let resolveRedirect = redirectToOnboard;
      if (resolveRedirect === "auto") {
        try {
          const { data: tenants } = await api.get(
            `/tenants?property_id=${activeProperty.id}`
          );
          resolveRedirect = (tenants || []).length === 0;
        } catch {
          resolveRedirect = false;
        }
      }
      const { data } = await api.post("/billing/subscribe", {
        property_id: activeProperty.id,
        origin_url: window.location.origin,
        redirect_after: resolveRedirect ? "onboard" : "",
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel auto-renewal? Access continues until the end of the period.")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel", { property_id: activeProperty.id });
      toast.success("Subscription canceled. Access continues to period end.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setBillingMode = async (mode) => {
    if (!activeProperty || details?.billing_mode === mode) return;
    setBusy(true);
    try {
      await api.patch(`/properties/${activeProperty.id}`, { billing_mode: mode });
      toast.success(
        mode === "invoice"
          ? "Switched to Invoice billing"
          : "Switched to Auto Pay billing"
      );
      await refreshProperties();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const payInvoice = async (iid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/invoices/${iid}/pay`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const resp = await api.get(`/billing/invoices/${inv.id}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!details) return <div className="text-sm text-[#71717A]">Loading…</div>;

  const sub = details.subscription || {};
  const setupPaid = details.setup_fee_paid;
  const isSubActive = sub.active;
  const monthlyTotal = details.monthly_total;
  const monthlyFull = details.monthly_full_total ?? monthlyTotal;
  const activeCars = details.active_cars ?? details.billable_units;
  const perCar = details.per_car ?? 1.0;
  const tierLabel = details.tier_label;
  const quoteRequired = !!details.quote_required;
  const inFreeMonth = !!details.free_month_active;
  const freeMonthUntil = details.free_month_until;
  const billingMode = details.billing_mode || "invoice";
  const isInvoiceMode = billingMode === "invoice";

  return (
    <div className="space-y-8">
      <div>
        <div className="mono-tiny text-[#71717A]">Billing</div>
        <div className="flex items-center gap-2 mt-2">
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            {isInvoiceMode ? "Invoices & billing" : "Setup & monthly billing"}
          </h1>
          <HelpIcon step={5} label="Show tutorial · billing" />
        </div>
        <p className="text-[#71717A] text-sm mt-2 max-w-2xl">
          One-time <b className="text-[#09090B]">$99 setup</b> + <b className="text-[#09090B]">$1.00 per car / month</b>. <b className="text-[#0F8F4F]">First 30 days free</b> after setup fee is paid.
          <span className="block mt-1">
            Volume discounts: 200–499 cars at $0.90, 500–999 at $0.85, 1000+ requires a custom quote.
          </span>
        </p>
      </div>

      {inFreeMonth && (
        <div
          className="bg-[#0F8F4F]/10 border border-[#0F8F4F]/30 rounded-md p-4 flex items-start gap-3"
          data-testid="billing-free-month-banner"
        >
          <Check className="w-5 h-5 text-[#0F8F4F] shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-sm text-[#0F8F4F]">
            <b>First month free</b> — no monthly charges through{" "}
            <b>{freeMonthUntil ? new Date(freeMonthUntil).toLocaleDateString() : "the end of your trial"}</b>.
            Your next paid invoice will be{" "}
            <b>${monthlyFull.toFixed(2)}</b>{" "}
            ({activeCars} active car{activeCars === 1 ? "" : "s"} × ${perCar.toFixed(2)}).
          </div>
        </div>
      )}

      {quoteRequired && (
        <div
          className="bg-[#FACC15]/10 border border-[#FACC15]/40 rounded-md p-4 flex items-start gap-3"
          data-testid="billing-quote-banner"
        >
          <CalendarClock className="w-5 h-5 text-[#92400E] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="text-sm text-[#92400E]">
            <b>Enterprise pricing required.</b> You have {activeCars} active
            cars, which exceeds our self-serve tier. The ScanMint team has
            been notified and will reach out shortly — or email{" "}
            <a href="mailto:support@scanmint.app" className="underline">
              support@scanmint.app
            </a>{" "}
            to expedite.
          </div>
        </div>
      )}

      {/* === Latest invoice hero (top-of-page shortcut) ===
          Puts the newest invoice PDF + Pay button front-and-centre so
          managers don't have to scroll past mode/subscription cards
          to find it. Falls back to a short empty-state hint. */}
      {invoices.length > 0 ? (() => {
        const latest = invoices[0];
        const isPaid = latest.status === "paid";
        const isProcessing = latest.status === "processing";
        return (
          <div
            className="bg-white border-2 border-[#0F8F4F]/40 rounded-xl p-5 md:p-6 flex items-center justify-between gap-4 flex-wrap shadow-sm"
            data-testid="billing-latest-invoice-hero"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-lg bg-[#0F8F4F]/10 flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6 text-[#0F8F4F]" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <div className="mono-tiny text-[#71717A]">
                  Latest invoice
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <span className="font-display font-bold text-xl font-mono">
                    {latest.invoice_number}
                  </span>
                  <InvoiceStatusPill status={latest.status} />
                </div>
                <div className="text-xs text-[#71717A] mt-1">
                  Issued {new Date(latest.issued_at).toLocaleDateString()} · Due{" "}
                  {new Date(latest.due_date).toLocaleDateString()} ·{" "}
                  <span className="font-semibold text-[#09090B]">
                    ${(latest.total || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => downloadInvoicePdf(latest)}
                variant="outline"
                className="h-11 border-[#0F8F4F]/40 text-[#0F8F4F] hover:bg-[#0F8F4F]/5"
                data-testid="billing-latest-invoice-pdf"
              >
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Download PDF
              </Button>
              {!isPaid && !isProcessing && (
                <Button
                  onClick={() => payInvoice(latest.id)}
                  disabled={busy}
                  className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                  data-testid="billing-latest-invoice-pay"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  Pay ${(latest.total || 0).toFixed(2)}
                </Button>
              )}
              {isProcessing && (
                <span className="text-xs text-[#1D4ED8] italic">
                  Bank payment clearing…
                </span>
              )}
            </div>
          </div>
        );
      })() : null}

      {/* === Invoice list (moved to top per user request Feb 2026) ===
          Full history table renders here, immediately below the
          latest-invoice hero, so PDFs are the first thing a manager
          sees on the Billing page. */}
      <div className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden" data-testid="invoice-list-card">
        <div className="flex items-center gap-2 p-5 border-b border-[#E4E4E7]">
          <Receipt className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
          <div className="mono-tiny text-[#71717A]">Invoices</div>
        </div>
        {invoices.length === 0 ? (
          <div className="p-6 text-sm text-[#71717A]" data-testid="invoices-empty">
            No invoices yet. {isInvoiceMode ? "Once you start your service (or on the 1st of next month), your invoice will appear here automatically." : "Switch to Invoice mode to receive monthly invoices."}
          </div>
        ) : (
          <div className="divide-y divide-[#E4E4E7]">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                data-testid={`invoice-row-${inv.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium font-mono">{inv.invoice_number}</span>
                    <InvoiceStatusPill status={inv.status} />
                  </div>
                  <div className="mono-tiny text-[#71717A] mt-1">
                    Issued {new Date(inv.issued_at).toLocaleDateString()} · Due {new Date(inv.due_date).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-[#3F3F46] mt-1">
                    {inv.setup_fee > 0 ? "$99 setup + " : ""}
                    {inv.quote_required ? (
                      <span className="text-[#92400E]">Enterprise quote ({inv.active_cars ?? inv.billable_units} cars)</span>
                    ) : (
                      <>
                        {inv.active_cars ?? inv.billable_units} car{(inv.active_cars ?? inv.billable_units) === 1 ? "" : "s"} × ${(inv.per_car ?? inv.per_unit ?? 1.0).toFixed(2)}
                        {inv.free_month_applied && <span className="ml-1 text-[#0F8F4F]">· first month free</span>}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                  <div className="font-display font-bold text-lg" data-testid={`invoice-total-${inv.id}`}>
                    ${(inv.total || 0).toFixed(2)}
                  </div>
                  <Button
                    onClick={() => downloadInvoicePdf(inv)}
                    variant="outline"
                    size="sm"
                    className="h-9"
                    data-testid={`invoice-download-${inv.id}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </Button>
                  {inv.status !== "paid" && inv.status !== "processing" && (
                    <Button
                      onClick={() => payInvoice(inv.id)}
                      disabled={busy}
                      size="sm"
                      className="h-9 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                      data-testid={`invoice-pay-${inv.id}`}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5 mr-1.5" />}
                      Pay invoice
                    </Button>
                  )}
                  {inv.status === "processing" && (
                    <span
                      className="text-xs text-[#1D4ED8] italic"
                      data-testid={`invoice-processing-${inv.id}`}
                    >
                      Bank payment clearing — typically 4–5 business days
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === Billing mode toggle === */}
      <div
        className="bg-white border border-[#E4E4E7] rounded-xl p-4 md:p-5 flex items-center justify-between gap-4 flex-wrap"
        data-testid="billing-mode-toggle"
      >
        <div className="flex items-start gap-3 min-w-0">
          <FileText className="w-5 h-5 text-[#0F8F4F] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="min-w-0">
            <div className="mono-tiny text-[#71717A]">Billing mode</div>
            <div className="font-medium">
              {isInvoiceMode ? "Invoice (default)" : "Auto Pay"}
            </div>
            <div className="text-xs text-[#71717A] mt-0.5">
              {isInvoiceMode
                ? "Generate one PDF invoice covering setup + monthly. Pay when ready."
                : "Charge a card on file each cycle via Stripe Checkout."}
            </div>
          </div>
        </div>
        <div
          className="inline-flex border border-[#E4E4E7] rounded-md overflow-hidden text-sm shrink-0"
          role="tablist"
        >
          <button
            type="button"
            disabled={busy}
            onClick={() => setBillingMode("invoice")}
            className={`px-4 py-2 transition-colors ${
              isInvoiceMode
                ? "bg-[#0F8F4F] text-white"
                : "bg-white text-[#3F3F46] hover:bg-[#F4F4F5]"
            }`}
            data-testid="billing-mode-invoice"
          >
            Invoice
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setBillingMode("auto_pay")}
            className={`px-4 py-2 transition-colors border-l border-[#E4E4E7] ${
              !isInvoiceMode
                ? "bg-[#0F8F4F] text-white"
                : "bg-white text-[#3F3F46] hover:bg-[#F4F4F5]"
            }`}
            data-testid="billing-mode-auto-pay"
          >
            Auto Pay
          </button>
        </div>
      </div>

      {/* === Suspended banner (auto-suspended for non-payment) === */}
      {details.billing_suspended_at && (
        <div
          className="rounded-xl p-5 md:p-6 border border-[#EF4444]/40 bg-[#FEE2E2]"
          data-testid="billing-suspended-banner"
        >
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-[#B91C1C] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="mono-tiny text-[#B91C1C]">Account suspended</div>
              <div className="font-display text-lg font-bold mt-0.5">
                Payment required — QR codes are inactive
              </div>
              <div className="text-sm text-[#7F1D1D] mt-1">
                Your invoice is more than 7 days overdue. Pay the open invoice
                below and your QR codes will reactivate automatically.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === Invoice mode: read-only manager view ===
          Invoices are auto-generated by the system on onboarding review and
          on the 1st of every month. Managers can pay or download — never
          create or mark paid. */}
      {isInvoiceMode && (
        <div
          className="rounded-xl p-6 border border-[#0F8F4F]/30 bg-[#0F8F4F]/5"
          data-testid="invoice-mode-info-card"
        >
          <div className="flex items-start gap-3">
            <Receipt className="w-5 h-5 text-[#0F8F4F] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="mono-tiny text-[#0F8F4F]">Invoice billing</div>
              <div className="font-display text-lg font-bold mt-0.5">
                Invoices are generated automatically
              </div>
              <div className="text-sm text-[#3F3F46] mt-1">
                Your first invoice is created the moment you start your
                service, and subsequent invoices are issued on the{" "}
                <b>1st of every month</b>. Pay online or download the PDF —
                only ScanMint Admin can mark an invoice as paid.
              </div>
              <Button
                onClick={() => nav("/dashboard/onboard/review")}
                variant="outline"
                size="sm"
                className="h-9 mt-3 border-[#0F8F4F] text-[#0F8F4F] hover:bg-[#0F8F4F] hover:text-white"
                data-testid="invoice-go-to-review"
              >
                View enrollment review
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* === Auto Pay mode: existing setup-fee + monthly cards === */}
      {!isInvoiceMode && (
        <>
      <div
        className={`rounded-xl p-6 md:p-8 border ${
          setupPaid
            ? "border-[#0F8F4F]/30 bg-[#0F8F4F]/5"
            : "border-[#FACC15]/40 bg-[#FFFBEA]"
        }`}
        data-testid="setup-fee-card"
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-md flex items-center justify-center shrink-0 ${
                setupPaid ? "bg-[#0F8F4F]/15" : "bg-[#FACC15]/20"
              }`}
            >
              {setupPaid ? (
                <Check className="w-6 h-6 text-[#0F8F4F]" strokeWidth={2} />
              ) : (
                <Package className="w-6 h-6 text-[#92400E]" strokeWidth={1.5} />
              )}
            </div>
            <div>
              <div className="font-display text-2xl md:text-3xl font-bold leading-tight">
                Activate Your Service
              </div>
              <div className="mono-tiny text-[#92400E] mt-1">
                Complete Setup — $99
              </div>
              <ul className="text-sm text-[#3F3F46] mt-3 space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-[#0F8F4F]" /> Inkless mini printer
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-[#0F8F4F]" /> QR code sticker supply
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-[#0F8F4F]" /> Welcome packet
                </li>
              </ul>
            </div>
          </div>
          <div className="text-right">
            {setupPaid ? (
              <>
                <div className="bg-[#0F8F4F] text-white text-xs font-bold px-3 py-1 rounded-full inline-block">
                  PAID
                </div>
                {details.setup_fee_paid_at && (
                  <div className="mono-tiny text-[#71717A] mt-2">
                    {new Date(details.setup_fee_paid_at).toLocaleDateString()}
                  </div>
                )}
              </>
            ) : (
              <Button
                onClick={paySetupFee}
                disabled={busy}
                className="h-12 px-6 bg-[#0F8F4F] hover:bg-[#0A6B3B] shadow-sm"
                data-testid="pay-setup-fee-btn"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Pay your set up fee
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* === Monthly billing card === */}
      <div
        className={`rounded-xl p-6 md:p-8 border ${
          isSubActive
            ? "border-[#0F8F4F]/30 bg-[#0F8F4F]/5"
            : "border-[#E4E4E7] bg-white"
        }`}
        data-testid="monthly-billing-card"
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="mono-tiny text-[#71717A]">Monthly billing</div>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                  isSubActive
                    ? "bg-[#0F8F4F]/15 text-[#0F8F4F] border-[#0F8F4F]/30"
                    : !setupPaid
                    ? "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]"
                    : sub.active_until
                    ? "bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/30"
                    : "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30"
                }`}
                data-testid="billing-status-badge"
              >
                {isSubActive
                  ? "Active"
                  : !setupPaid
                  ? "Locked"
                  : sub.active_until
                  ? "Expired"
                  : "Ready"}
              </span>
            </div>
            <div className="font-display text-3xl md:text-4xl font-bold mt-2">
              {isSubActive
                ? "Active"
                : setupPaid
                ? sub.active_until
                  ? "Expired"
                  : "Ready"
                : "Locked"}
            </div>
            {isSubActive && (
              <div className="text-sm text-[#71717A] mt-2 flex items-center gap-2">
                <CalendarClock className="w-4 h-4" strokeWidth={1.5} />
                Renews{" "}
                {sub.auto_renew === false ? "(canceled — ends " : "on "}
                {new Date(sub.active_until).toLocaleDateString()}
                {sub.auto_renew === false ? ")" : ""}
              </div>
            )}
            {!isSubActive && setupPaid && activeCars > 0 && !quoteRequired && !inFreeMonth && (
              <div className="text-sm text-[#71717A] mt-2">
                Click <b>Pay this month</b> to charge ${monthlyTotal.toFixed(2)} for the next 30 days.
              </div>
            )}
            {!isSubActive && setupPaid && activeCars === 0 && (
              <div className="text-sm text-[#71717A] mt-2">
                Add at least one car to begin monthly billing.
              </div>
            )}
            {!setupPaid && (
              <div className="text-sm text-[#92400E] mt-2 flex items-center gap-2">
                <Lock className="w-4 h-4" strokeWidth={1.5} />
                Pay the $99 setup fee above to unlock monthly billing.
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="mono-tiny text-[#71717A]">This cycle</div>
            <div className="font-display text-4xl font-bold mt-1" data-testid="billing-total">
              {quoteRequired ? "Quote" : `$${monthlyTotal.toFixed(2)}`}
            </div>
            <div className="text-sm text-[#71717A]" data-testid="billing-equation">
              <span data-testid="billing-units-line">
                {activeCars} car{activeCars === 1 ? "" : "s"}
              </span>{" "}
              {quoteRequired ? (
                <>· <b className="text-[#92400E]">enterprise — request a quote</b></>
              ) : inFreeMonth ? (
                <>× ${perCar.toFixed(2)} = <s className="text-[#71717A]">${monthlyFull.toFixed(2)}</s>{" "}
                <b className="text-[#0F8F4F]">FREE this month</b></>
              ) : (
                <>× ${perCar.toFixed(2)} = <b className="text-[#09090B]">${monthlyTotal.toFixed(2)} / month</b></>
              )}
            </div>
            {tierLabel && !quoteRequired && activeCars > 0 && (
              <div className="mono-tiny text-[#71717A] mt-1">{tierLabel}</div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {setupPaid && !isSubActive && activeCars > 0 && !quoteRequired && !inFreeMonth && (
            <Button
              onClick={() => payMonthly()}
              disabled={busy}
              className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
              data-testid="pay-monthly-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Pay this month · ${monthlyTotal.toFixed(2)}
            </Button>
          )}
          {isSubActive && (
            <>
              <Button
                onClick={() => payMonthly({ redirectToOnboard: false })}
                disabled={busy}
                variant="outline"
                className="h-11"
                data-testid="renew-btn"
              >
                Renew now
              </Button>
              {sub.auto_renew !== false && (
                <Button
                  onClick={cancel}
                  disabled={busy}
                  variant="ghost"
                  className="h-11 text-[#EF4444] hover:bg-[#EF4444]/10 hover:text-[#EF4444]"
                  data-testid="cancel-subscription-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" /> Cancel auto-renew
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* === Units breakdown === */}
      {details.units_breakdown && details.units_breakdown.length > 0 && (
        <div
          className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden"
          data-testid="units-breakdown-card"
        >
          <div className="flex items-center gap-2 p-5 border-b border-[#E4E4E7]">
            <Building2 className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
            <div className="mono-tiny text-[#71717A]">Units billed this cycle</div>
          </div>
          <div className="divide-y divide-[#E4E4E7]">
            {details.units_breakdown.map((u, i) => (
              <div
                key={`${u.building || "_"}-${u.unit_number}-${i}`}
                className="p-4 md:p-5 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="font-medium">
                    {u.building ? `Bldg ${u.building} · ` : ""}Unit {u.unit_number}
                  </div>
                  <div className="mono-tiny text-[#71717A]">
                    {u.vehicle_count} car{u.vehicle_count === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="font-display font-bold text-[#0F8F4F]">
                  ${(perCar * u.vehicle_count).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === Payment method === */}
      <div
        className="bg-white border border-[#E4E4E7] rounded-lg p-6 flex items-center justify-between flex-wrap gap-4"
        data-testid="payment-method-card"
      >
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
          <div>
            <div className="mono-tiny text-[#71717A]">Payment method</div>
            {sub.card_brand && sub.card_last4 ? (
              <>
                <div className="font-medium" data-testid="payment-method">
                  {sub.card_brand} ending in {sub.card_last4}
                </div>
                <div className="mono-tiny text-[#71717A] mt-1 tracking-wider" data-testid="payment-method-masked">
                  {sub.card_masked}
                </div>
              </>
            ) : (
              <>
                <div className="font-medium" data-testid="payment-method">
                  None on file
                </div>
                <div className="mono-tiny text-[#71717A] mt-1">
                  Payment method is collected at checkout — ScanMint does not
                  retain your card.
                </div>
              </>
            )}
          </div>
        </div>
        {/* Edit payment method routes the user to whatever Stripe Checkout
            represents the next legitimate charge — monthly billing if the
            subscription is active, or setup-fee checkout if setup hasn't
            been paid yet. When there's no card on file we ALWAYS surface
            an "Add card" affordance so the user is never stuck without a
            way to save one. */}
        {sub.card_brand && sub.card_last4 ? (
          isSubActive && (
            <Button
              onClick={() => payMonthly({ redirectToOnboard: false })}
              disabled={busy}
              variant="outline"
              className="h-10 border-[#0F8F4F] text-[#0F8F4F] hover:bg-[#0F8F4F] hover:text-white"
              data-testid="edit-payment-method-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Edit payment method
            </Button>
          )
        ) : (
          <Button
            onClick={
              !setupPaid
                ? paySetupFee
                : () => payMonthly({ redirectToOnboard: false })
            }
            disabled={busy}
            className="h-10 bg-[#0F8F4F] hover:bg-[#0A6B3B] text-white"
            data-testid="add-payment-method-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {!setupPaid ? "Add payment method" : "Add card"}
          </Button>
        )}
        {/* Remove the saved card on file. Only shown when the payment-method
            panel actually displays a card brand/last4 — otherwise there's
            nothing to remove. Also wipes card metadata from past invoices. */}
        {sub.card_brand && sub.card_last4 && (
          <Button
            onClick={removePaymentMethod}
            disabled={busy}
            variant="outline"
            className="h-10 border-[#E4E4E7] text-[#3F3F46] hover:bg-red-50 hover:border-red-300 hover:text-red-700"
            data-testid="remove-payment-method-btn"
            title="Forget saved card from this property and past invoices"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Remove saved card
          </Button>
        )}
      </div>
        </>
      )}

      {/* === Auto Pay billing history (only for auto-pay mode) === */}
      {!isInvoiceMode && (
      <div className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden" data-testid="auto-pay-history">
        <div className="flex items-center gap-2 p-5 border-b border-[#E4E4E7]">
          <Receipt className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
          <div className="mono-tiny text-[#71717A]">Auto-pay history</div>
        </div>
        {details.invoices.length === 0 ? (
          <div className="p-6 text-sm text-[#71717A]">No payments yet.</div>
        ) : (
          <div className="divide-y divide-[#E4E4E7]">
            {details.invoices.map((inv) => (
              <div
                key={inv.id || inv.session_id}
                className="p-4 md:p-5 flex items-center justify-between"
                data-testid={`autopay-row-${inv.session_id}`}
              >
                <div>
                  <div className="font-medium">{invoiceLabel(inv.kind)}</div>
                  <div className="mono-tiny text-[#71717A]">
                    {new Date(inv.paid_at).toLocaleString()}
                  </div>
                </div>
                <div className="font-display font-bold">${(inv.amount || 0).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function InvoiceStatusPill({ status }) {
  const map = {
    pending: { label: "Pending", cls: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30" },
    issued: { label: "Issued", cls: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30" },
    sent: { label: "Sent", cls: "bg-[#DBEAFE] text-[#1E40AF] border-[#3B82F6]/30" },
    processing: { label: "Payment processing", cls: "bg-[#EFF6FF] text-[#1D4ED8] border-[#3B82F6]/40" },
    paid: { label: "Paid", cls: "bg-[#0F8F4F]/15 text-[#0F8F4F] border-[#0F8F4F]/30" },
    overdue: { label: "Overdue", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/30" },
    void: { label: "Void", cls: "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]" },
  };
  const m = map[status] || map.pending;
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${m.cls}`}
      data-testid={`invoice-status-${status}`}
    >
      {m.label}
    </span>
  );
}

function invoiceLabel(kind) {
  if (kind === "setup_fee") return "ScanMint Starter setup fee";
  if (kind === "subscription") return "Monthly billing";
  if (kind === "sticker_pack") return "Sticker pack order";
  return kind || "Charge";
}
